import { Component, type ReactNode } from 'react'
import { withTranslation, type WithTranslation } from 'react-i18next'

import { env } from '@maxus/website/constants'

interface ErrorBoundaryProps extends WithTranslation {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // 开发环境下将错误输出到控制台。
    console.error('Error Boundary caught an error:', error, errorInfo)

    // 如提供了自定义错误处理器则调用它。
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render() {
    const { t } = this.props

    if (this.state.hasError) {
      // 如提供了自定义降级 UI 则使用它。
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isDev = env.VITE_NODE_ENV === 'development'

      // 默认降级 UI。
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            backgroundColor: '#f5f5f5',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '40px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '48px',
                marginBottom: '20px',
              }}
            >
              ⚠️
            </div>

            <h1
              style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#262626',
                marginBottom: '12px',
              }}
            >
              {t('error.title')}
            </h1>

            <p
              style={{
                fontSize: '14px',
                color: '#8c8c8c',
                marginBottom: '24px',
                lineHeight: '1.5',
              }}
            >
              {isDev ? this.state.error?.message : t('error.message')}
            </p>

            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
              }}
            >
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '8px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1890ff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#40a9ff')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = '#1890ff')}
              >
                {t('error.refresh')}
              </button>

              <button
                onClick={this.handleReset}
                style={{
                  padding: '8px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#595959',
                  backgroundColor: 'white',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.borderColor = '#40a9ff'
                  e.currentTarget.style.color = '#40a9ff'
                }}
                onMouseOut={e => {
                  e.currentTarget.style.borderColor = '#d9d9d9'
                  e.currentTarget.style.color = '#595959'
                }}
              >
                {t('error.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryClass)
